import { AlertCircle, AlertTriangle, CheckCircle2 } from 'lucide-react';
import React, { useMemo } from 'react';

import { cn } from '@/utils';

import type { CaseItem } from '../data/types';

interface AggregateTileProps {
  agentName: string;
  columnLabel: string;
  teamLabel?: string;
  cases: CaseItem[];
  onClick?: () => void;
}

/**
 * The clickable tile rendered in an aggregate-cell. Summarises count +
 * breached / at-risk / safe lines; clicking it opens the per-team popover
 * (the `AggregateRowList` lives inside `AggregateCell`'s `Popover` body).
 *
 * Implemented as a presentational button — the `onClick` is wired by the
 * parent `<Popover>` via Radix's `PopoverTrigger` (`asChild`), which is why
 * we forward props/ref through the underlying button. The parent passes its
 * own click handler.
 */
export const AggregateTile = React.forwardRef<HTMLButtonElement, AggregateTileProps>(
  ({ agentName, columnLabel, teamLabel, cases, onClick, ...rest }, ref) => {
    // Single-pass roll-up instead of three `.filter().length` scans —
    // matters per-render when the tile is one of many in a polling grid.
    const { breached, atRisk, safe } = useMemo(() => {
      let b = 0;
      let w = 0;
      for (const c of cases) {
        if (c.riskLevel === 'breached') b++;
        else if (c.riskLevel === 'warning') w++;
      }
      return { breached: b, atRisk: w, safe: cases.length - b - w };
    }, [cases]);

    return (
      <button
        ref={ref}
        type="button"
        onClick={onClick}
        className={cn(
          'w-full text-left flex flex-col gap-2 rounded-md border border-border bg-card p-3 cursor-pointer',
          'hover:border-foreground/30 hover:bg-accent/30 transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        )}
        aria-label={
          teamLabel
            ? `Expand ${cases.length} cases for ${agentName} in ${teamLabel} (${columnLabel})`
            : `Expand ${cases.length} cases for ${agentName} in ${columnLabel}`
        }
        {...rest}
      >
        <div className="flex items-baseline justify-between">
          <span className="text-2xl font-semibold tabular-nums">{cases.length}</span>
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Cases</span>
        </div>
        {teamLabel && (
          <div className="text-[11px] font-medium text-foreground truncate" title={teamLabel}>
            {teamLabel}
          </div>
        )}
        {/* Per-state summary lines. Each state gets a distinct icon SHAPE
            (not just colour) so the signal survives without colour
            perception — matching the per-card / per-row icons used in
            `CaseCard` and `AggregateRowList`. */}
        <div className="flex flex-col gap-0.5 text-[11px]">
          {breached > 0 && (
            <div className="flex items-center gap-1.5">
              <AlertCircle
                className="size-3 text-critical-foreground shrink-0"
                aria-hidden="true"
              />
              <span className="text-muted-foreground">
                <span className="text-critical-foreground font-medium">{breached}</span> breached
              </span>
            </div>
          )}
          {atRisk > 0 && (
            <div className="flex items-center gap-1.5">
              <AlertTriangle
                className="size-3 text-attention-foreground shrink-0"
                aria-hidden="true"
              />
              <span className="text-muted-foreground">
                <span className="text-attention-foreground font-medium">{atRisk}</span> at risk
              </span>
            </div>
          )}
          {safe > 0 && (
            <div className="flex items-center gap-1.5">
              <CheckCircle2
                className="size-3 text-positive-foreground shrink-0"
                aria-hidden="true"
              />
              <span className="text-muted-foreground">
                <span className="text-foreground font-medium">{safe}</span> safe
              </span>
            </div>
          )}
        </div>
      </button>
    );
  },
);

AggregateTile.displayName = 'AggregateTile';
